/**
 * User Data Helper
 * 
 * Provides utilities for accessing user-scoped data in Firestore.
 * Each user's data is stored under: users/{userId}/{collection}
 * 
 * This ensures complete data isolation between users.
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { db, auth, functions } from './firebase-config';
import { httpsCallable } from 'firebase/functions';
import { hasPaidAccess, FREE_PLAN_LIMITS } from './entitlements';
import { normalizeRecipe, scaleRecipe, getRecipeLye, getRecipeBatchMass, additiveGrams } from './recipeSchema';
import { quantity, resolveInventoryItem, planPurchase, planPurchaseReversal, planStockMovements, planFinishedSale, getBatchCureDays } from './workflowPlanning';

/**
 * Get the current user's ID
 * @throws Error if user is not authenticated
 */
export const getCurrentUserId = () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.uid;
};

/**
 * Get a reference to a user's subcollection
 * Path: users/{userId}/{collectionName}
 */
export const getUserCollection = (collectionName) => {
  const userId = getCurrentUserId();
  return collection(db, 'users', userId, collectionName);
};

/**
 * Get a reference to a specific document in a user's subcollection
 * Path: users/{userId}/{collectionName}/{docId}
 */
export const getUserDoc = (collectionName, docId) => {
  const userId = getCurrentUserId();
  return doc(db, 'users', userId, collectionName, docId);
};

/**
 * Add a document to a user's subcollection
 */
export const addUserDoc = async (collectionName, data) => {
  if (collectionName === 'purchases') return recordPurchase(data);
  if (collectionName === 'batches') return createProductionBatch(data);
  if (collectionName === 'sales') return recordSale(data);
  if (QUOTA_COLLECTIONS.includes(collectionName)) {
    await ensureUsageInitialized();
    const resourceRef = doc(getUserCollection(collectionName));
    return runTransaction(db, async transaction => {
      const usage = await readUsageChange(transaction, collectionName, 1, resourceRef.id);
      transaction.set(resourceRef, { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      writeUsageChange(transaction, usage);
      return resourceRef;
    });
  }
  const colRef = getUserCollection(collectionName);
  return addDoc(colRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Update a document in a user's subcollection
 */
export const updateUserDoc = async (collectionName, docId, data) => {
  if (collectionName === 'batches') return updateProductionBatch(docId, data);
  if (['ingredients', 'packaging'].includes(collectionName)) return runTransaction(db, async transaction => {
    const reference = getUserDoc(collectionName, docId);
    const existing = await transaction.get(reference);
    if (!existing.exists()) throw new Error('This inventory item no longer exists.');
    const current = existing.data();
    if (current.stockLedgerVersion === 1 && data.currencyCode && data.currencyCode !== (current.currencyCode || 'INR')) throw new Error('This item has linked stock movements. Keep its current costing currency to preserve purchase and production reversals.');
    const { stockLedgerVersion: ignoredVersion, linkedRecordCount: ignoredCount, ...editable } = data;
    transaction.update(reference, { ...editable, updatedAt: serverTimestamp() });
  });
  if (['purchases', 'sales'].includes(collectionName) && Object.keys(data).some(key => ['quantity', 'totalCost', 'costPerUnit', 'pricePerUnit', 'batchId', 'ingredientId'].includes(key))) throw new Error('Delete and recreate this record to safely reverse its stock movements before changing quantities or costs.');
  const docRef = getUserDoc(collectionName, docId);
  return updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Delete a document from a user's subcollection
 */
export const deleteUserDoc = async (collectionName, docId) => {
  if (['batches', 'purchases', 'sales'].includes(collectionName)) return reverseWorkflowRecord(collectionName, docId);
  if (QUOTA_COLLECTIONS.includes(collectionName)) {
    await ensureUsageInitialized();
    const resourceRef = getUserDoc(collectionName, docId);
    return runTransaction(db, async transaction => {
      const existing = await transaction.get(resourceRef);
      if (!existing.exists()) return;
      if (collectionName === 'ingredients' && Number(existing.data().linkedRecordCount || 0) > 0) throw new Error('This ingredient has linked purchases or production batches. Reverse those records before deleting it.');
      const usage = await readUsageChange(transaction, collectionName, -1, docId);
      transaction.delete(resourceRef);
      writeUsageChange(transaction, usage);
    });
  }
  if (collectionName === 'packaging') return runTransaction(db, async transaction => {
    const reference = getUserDoc(collectionName, docId);
    const existing = await transaction.get(reference);
    if (!existing.exists()) return;
    if (Number(existing.data().linkedRecordCount || 0) > 0) throw new Error('This packaging has linked production batches. Reverse those batches before deleting it.');
    transaction.delete(reference);
  });
  const docRef = getUserDoc(collectionName, docId);
  return deleteDoc(docRef);
};

/**
 * Get all documents from a user's subcollection
 */
export const getUserDocs = async (collectionName) => {
  const colRef = getUserCollection(collectionName);
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
};

/**
 * Get a single document from a user's subcollection
 */
export const getSingleUserDoc = async (collectionName, docId) => {
  const docRef = getUserDoc(collectionName, docId);
  const snapshot = await getDoc(docRef);
  if (snapshot.exists()) {
    return { ...snapshot.data(), id: snapshot.id };
  }
  return null;
};

/**
 * Subscribe to real-time updates on a user's subcollection
 * Returns unsubscribe function
 */
export const subscribeToUserCollection = (collectionName, callback, queryConstraints = []) => {
  try {
    const colRef = getUserCollection(collectionName);
    const q = queryConstraints.length > 0 
      ? query(colRef, ...queryConstraints) 
      : colRef;
    
    return onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      callback(docs);
    }, (error) => {
      console.error(`Error subscribing to ${collectionName}:`, error);
      callback([]);
    });
  } catch (error) {
    console.error(`Error setting up subscription for ${collectionName}:`, error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Subscribe to a single document in a user's subcollection
 */
export const subscribeToUserDoc = (collectionName, docId, callback) => {
  try {
    const docRef = getUserDoc(collectionName, docId);
    return onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        callback({ ...snapshot.data(), id: snapshot.id });
      } else {
        callback(null);
      }
    });
  } catch (error) {
    console.error(`Error subscribing to document:`, error);
    return () => {};
  }
};

// Export query helpers
export { query, where, orderBy, limit };

/**
 * Collection names used in Soap Lab
 */
export const COLLECTIONS = {
  RECIPES: 'recipes',
  INGREDIENTS: 'ingredients',
  BATCHES: 'batches',
  SALES: 'sales',
  NOTIFICATIONS: 'notifications',
  NOTES: 'notes',
  SETTINGS: 'settings',
};

const userDataHelper = {
  getCurrentUserId,
  getUserCollection,
  getUserDoc,
  addUserDoc,
  updateUserDoc,
  deleteUserDoc,
  getUserDocs,
  getSingleUserDoc,
  subscribeToUserCollection,
  subscribeToUserDoc,
  COLLECTIONS,
};
export default userDataHelper;

// Quotas are coupled to resource writes so concurrent tabs cannot exceed limits.
export const QUOTA_COLLECTIONS = ['recipes', 'ingredients', 'batches'];
const usageReference = () => getUserDoc('system', 'usage');
export const ensureUsageInitialized = async () => {
  const snapshot = await getDoc(usageReference());
  if (!snapshot.exists()) {
    try { await httpsCallable(functions, 'initializeUsage')({}); }
    catch (error) { throw new Error(`Could not initialize account limits. Please try again after the backend update is deployed. ${error.message || ''}`); }
  }
};
export const readUsageChange = async (transaction, collectionName, delta, id) => {
  const reference = usageReference();
  const snapshot = await transaction.get(reference);
  if (!snapshot.exists()) throw new Error('Account limits are not initialized. Refresh and try again.');
  const current = Number(snapshot.data()[collectionName]);
  if (delta > 0) {
    const profile = await transaction.get(doc(db, 'users', getCurrentUserId()));
    const maximum = { recipes: FREE_PLAN_LIMITS.maxRecipes, ingredients: FREE_PLAN_LIMITS.maxIngredients, batches: FREE_PLAN_LIMITS.maxBatches }[collectionName];
    if (!hasPaidAccess(profile.exists() ? profile.data() : null) && current + delta > maximum) throw new Error(`Your free plan supports ${maximum} ${collectionName}. Remove an existing item or upgrade before adding another.`);
  }
  if (!Number.isInteger(current) || current < 0 || current + delta < 0) throw new Error('Account resource counts need reconciliation before this change.');
  return { reference, collectionName, next: current + delta, lastMutation: { collection: collectionName, id, operation: delta > 0 ? 'create' : 'delete' } };
};
export const writeUsageChange = (transaction, change) => {
  if (change) transaction.update(change.reference, { [change.collectionName]: change.next, lastMutation: change.lastMutation, updatedAt: serverTimestamp() });
};
const cleanObject = value => JSON.parse(JSON.stringify(value, (key, item) => item === undefined ? null : item));
const recordsForRequirements = async (transaction, requirements) => {
  const records = {};
  const unique = new Map(requirements.map(item => [`${item.collection || 'ingredients'}/${item.id}`, item]));
  for (const item of unique.values()) {
    const collectionName = item.collection || 'ingredients';
    const snapshot = await transaction.get(getUserDoc(collectionName, item.id));
    if (!snapshot.exists()) throw new Error(`${item.name || 'Linked stock item'} no longer exists. Restore or relink it before changing this record.`);
    (records[collectionName] ||= []).push({ ...snapshot.data(), ...(collectionName === 'packaging' && !snapshot.data().unit ? { unit: 'pcs' } : {}), id: snapshot.id });
  }
  return records;
};
const applyStockUpdates = (transaction, updates) => updates.forEach(update => transaction.update(getUserDoc(update.collection, update.id), { ...update.patch, stockLedgerVersion: 1, updatedAt: serverTimestamp() }));

export const recordPurchase = async data => {
  if (!data.ingredientId) throw new Error('Select an existing ingredient. Add new ingredients in Inventory before recording a purchase.');
  const purchaseRef = doc(getUserCollection('purchases'));
  return runTransaction(db, async transaction => {
    const ingredientRef = getUserDoc('ingredients', data.ingredientId);
    const snapshot = await transaction.get(ingredientRef);
    if (!snapshot.exists()) throw new Error('The selected ingredient no longer exists.');
    const ingredient = { ...snapshot.data(), id: snapshot.id };
    const plan = planPurchase(ingredient, data);
    transaction.update(ingredientRef, { ...plan.patch, stockLedgerVersion: 1, purchaseDate: data.date, updatedAt: serverTimestamp() });
    transaction.set(purchaseRef, { ...cleanObject({ ...data, ingredientName: ingredient.name, costPerUnit: Number(data.totalCost) / Number(data.quantity), inventoryApplied: true, stockLedgerVersion: 1, stockMovement: plan.movement }), createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    return purchaseRef;
  });
};

const resolveAlkali = (recipe, type, ingredients) => {
  const selected = recipe.alkaliSelections?.[type] || (!recipe.dualLye ? recipe.alkaliSelection || recipe.alkali : null);
  if (selected && (typeof selected === 'string' || selected.id || selected.key || selected.name)) return resolveInventoryItem(typeof selected === 'string' ? { id: selected, key: selected } : selected, ingredients, type);
  const matches = ingredients.filter(item => {
    const name = `${item.name || ''} ${item.key || ''}`.toLowerCase();
    return type === 'KOH' ? /\bkoh\b|potassium hydroxide/.test(name.replace(/_/g, ' ')) : /\bnaoh\b|sodium hydroxide/.test(name.replace(/_/g, ' '));
  });
  if (matches.length !== 1) throw new Error(`Link one ${type} inventory ingredient in the recipe before producing this batch.`);
  return matches[0];
};
export const buildProductionRequirements = (recipe, bars, ingredients, packagingItems, packagingOverride) => {
  const requirements = recipe.items.filter(item => Number(item.weight) > 0).map(item => {
    const ingredient = resolveInventoryItem(item, ingredients);
    return { collection: 'ingredients', id: ingredient.id, name: ingredient.name, quantity: item.weight, unit: 'g' };
  });
  recipe.additives.forEach(item => {
    const grams = additiveGrams(item);
    if (!Number.isFinite(grams) || grams < 0) throw new Error(`Set a valid mass or density for ${item.name || 'additive'} before production.`);
    if (grams === 0) return;
    const ingredient = resolveInventoryItem(item, ingredients);
    requirements.push({ collection: 'ingredients', id: ingredient.id, name: ingredient.name, quantity: grams, unit: 'g' });
  });
  const lye = getRecipeLye(recipe);
  const alkalis = recipe.alkaliWeights || {};
  if (lye > 0 && Math.abs(Number(alkalis.NaOH || 0) + Number(alkalis.KOH || 0) - lye) > 0.01) throw new Error('This older dual-lye recipe has no reliable alkali split. Open and save it in the calculator before production.');
  ['NaOH', 'KOH'].forEach(type => {
    const grams = Number(alkalis[type] || 0);
    if (grams <= 0) return;
    const ingredient = resolveAlkali(recipe, type, ingredients);
    requirements.push({ collection: 'ingredients', id: ingredient.id, name: ingredient.name, quantity: grams, unit: 'g' });
  });
  if (recipe.waterSelection && Number(recipe.waterWeight) > 0) {
    const ingredient = resolveInventoryItem(recipe.waterSelection, ingredients, 'Water');
    requirements.push({ collection: 'ingredients', id: ingredient.id, name: ingredient.name, quantity: recipe.waterWeight, unit: 'g' });
  }
  const packaging = packagingOverride === null ? null : packagingOverride || recipe.packagingSelection;
  if (packaging?.id) {
    const item = resolveInventoryItem(packaging, packagingItems, 'Packaging');
    const perBar = Number(packaging.quantityPerBar ?? 1);
    quantity(perBar, 'Packaging per bar', { positive: true });
    if (!['pcs', 'piece', 'pc', 'pieces', 'unit', 'units'].includes(String(item.unit || 'pcs').toLowerCase()) && packaging.quantityPerBar === undefined) throw new Error('Weight/volume packaging needs an explicit quantity per bar. Select piece-based packaging or set its quantity per bar.');
    requirements.push({ collection: 'packaging', id: item.id, name: item.name, quantity: bars * perBar, unit: packaging.unit || item.unit || 'pcs' });
  }
  return requirements;
};
export const createProductionBatch = async data => {
  if (!data.recipeId) throw new Error('Select a saved recipe before creating a batch.');
  await ensureUsageInitialized();
  // Inventory enumeration resolves legacy keys once; quantities are re-read inside the transaction.
  const [ingredients, packaging] = await Promise.all([getUserDocs('ingredients'), getUserDocs('packaging')]);
  const batchRef = doc(getUserCollection('batches'));
  return runTransaction(db, async transaction => {
    const recipeSnapshot = await transaction.get(getUserDoc('recipes', data.recipeId));
    if (!recipeSnapshot.exists()) throw new Error('The selected recipe no longer exists.');
    const saved = normalizeRecipe({ ...recipeSnapshot.data(), id: recipeSnapshot.id });
    const baseBars = quantity(saved.bars, 'Recipe bar yield', { positive: true });
    const factor = data.multiplier !== undefined ? quantity(data.multiplier, 'Batch multiplier', { positive: true }) : quantity(data.bars ?? baseBars, 'Batch bars', { positive: true, integer: true }) / baseBars;
    const recipe = scaleRecipe(saved, factor);
    const bars = quantity(data.bars ?? Math.max(1, Math.floor(baseBars * factor)), 'Batch bars', { positive: true, integer: true });
    if (!recipe.items.length || recipe.items.some(item => !Number.isFinite(item.weight) || item.weight < 0) || Math.abs(recipe.items.reduce((sum, item) => sum + Number(item.pct), 0) - 100) > 0.1) throw new Error('A production recipe must contain valid oils totaling 100%. Open it in the calculator first.');
    // Melt-and-pour starts from a premade base, so it legitimately has no lye or water.
    if (recipe.recipeType !== 'melt_pour' && (!(getRecipeLye(recipe) > 0) || !(Number(recipe.waterWeight) > 0))) throw new Error('This recipe is missing reliable alkali or water amounts. Open and save it in the calculator before production.');
    const recordOnly = data.deductStock === false;
    const requirements = recordOnly ? [] : buildProductionRequirements(recipe, bars, ingredients, packaging, data.packagingSelection);
    if (requirements.length > 200) throw new Error('This batch has too many stock items. Limit a recipe to 200 linked inventory items.');
    const records = await recordsForRequirements(transaction, requirements);
    const stock = planStockMovements(requirements, records);
    const usage = await readUsageChange(transaction, 'batches', 1, batchRef.id);
    const currencies = new Set(stock.movements.map(item => item.currencyCode));
    const currencyCode = saved.currencyCode || 'INR';
    if (currencies.size && (currencies.size > 1 || !currencies.has(currencyCode))) throw new Error(`Use ${currencyCode} for all linked ingredient and packaging costs before production. Mixed currencies cannot be summed safely.`);
    const materialCost = stock.movements.filter(item => item.collection === 'ingredients').reduce((sum, item) => sum + item.totalCost, 0);
    const packagingTracked = stock.movements.some(item => item.collection === 'packaging');
    const packagingCost = packagingTracked ? stock.movements.filter(item => item.collection === 'packaging').reduce((sum, item) => sum + item.totalCost, 0) : quantity(recipe.cost?.packaging ?? 0, 'Estimated packaging cost');
    const workingCost = quantity(recipe.cost?.working ?? 0, 'Working cost');
    const totalCost = recordOnly ? Number(recipe.totals?.totalCost || 0) : materialCost + packagingCost + workingCost;
    const made = new Date(`${data.madeDate || ''}T12:00:00`);
    if (Number.isNaN(made.getTime())) throw new Error('Choose a valid production date.');
    const cureDays = getBatchCureDays({}, recipe);
    const ready = new Date(made); ready.setDate(ready.getDate() + cureDays);
    const expiry = new Date(ready); expiry.setMonth(expiry.getMonth() + Number(data.soapExpiryMonths || 12));
    const dateString = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const notes = Array.isArray(data.notes) ? data.notes : data.notes ? [{ text: String(data.notes), date: new Date().toISOString() }] : [];
    const batch = cleanObject({ ...data, recipeId: saved.id, recipeName: saved.name, name: data.name || `${saved.name || 'Batch'} - ${data.madeDate}`, status: 'curing', bars, remainingBars: bars, soldBars: 0, multiplier: factor, batchMass: getRecipeBatchMass(recipe), recipeSnapshot: recipe, costSnapshot: { material: recordOnly ? recipe.cost?.material || 0 : materialCost, packaging: recordOnly ? recipe.cost?.packaging || 0 : packagingCost, working: workingCost, total: totalCost }, costPerBar: totalCost / bars, totalCost, currencyCode, cureDays, readyDate: dateString(ready), soapExpiryDate: dateString(expiry), notes, inventoryApplied: !recordOnly, inventoryStatus: recordOnly ? 'record_only' : 'applied', packagingTracked, packagingCostEstimated: !packagingTracked, waterTracked: Boolean(recipe.waterSelection), stockLedgerVersion: 1, stockMovements: stock.movements });
    applyStockUpdates(transaction, stock.updates);
    transaction.set(batchRef, { ...batch, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    writeUsageChange(transaction, usage);
    return batchRef;
  });
};
export const updateProductionBatch = async (id, updates) => runTransaction(db, async transaction => {
  const reference = getUserDoc('batches', id);
  const snapshot = await transaction.get(reference);
  if (!snapshot.exists()) throw new Error('This batch no longer exists.');
  const batch = snapshot.data();
  if (Object.keys(updates).some(key => ['recipeId', 'bars', 'multiplier', 'stockMovements', 'inventoryApplied', 'remainingBars', 'soldBars', 'recipeSnapshot'].includes(key))) throw new Error('Production quantities and stock history cannot be edited. Delete an unsold batch to reverse it, then create a corrected batch.');
  if (updates.status === 'sold' && Number(batch.remainingBars) !== 0) throw new Error('Record sales to mark a batch sold and deduct its finished stock.');
  if (updates.status === 'planned' && batch.inventoryApplied) throw new Error('This batch has consumed ingredients. Delete the unsold batch to reverse production before planning it again.');
  transaction.update(reference, { ...updates, updatedAt: serverTimestamp() });
});
export const recordSale = async data => {
  quantity(data.quantity, 'Sale quantity', { positive: true, integer: true });
  const price = quantity(data.pricePerUnit, 'Selling price', { positive: true });
  const saleRef = doc(getUserCollection('sales'));
  return runTransaction(db, async transaction => {
    let batch = null;
    let batchRef;
    let patch;
    if (data.batchId) {
      batchRef = getUserDoc('batches', data.batchId);
      const snapshot = await transaction.get(batchRef);
      if (!snapshot.exists()) throw new Error('The selected batch no longer exists.');
      batch = { ...snapshot.data(), id: snapshot.id };
      patch = planFinishedSale(batch, data);
      if (data.currencyCode && data.currencyCode !== (batch.currencyCode || 'INR')) throw new Error('The sale and production batch must use the same currency.');
    }
    const costPerUnit = batch ? quantity(batch.costPerBar ?? 0, 'Batch cost per bar') : quantity(data.costPerUnit ?? 0, 'Unit cost');
    const sale = cleanObject({ ...data, recipeId: batch?.recipeId || data.recipeId || null, costPerUnit, totalAmount: Number(data.quantity) * price, profit: Number(data.quantity) * (price - costPerUnit), inventoryApplied: Boolean(batch), inventoryStatus: batch ? 'finished_stock_applied' : 'unlinked', stockLedgerVersion: 1, batchSnapshot: batch ? { id: batch.id, name: batch.name, recipeId: batch.recipeId, costPerBar: batch.costPerBar, currencyCode: batch.currencyCode || 'INR' } : null });
    if (batch) transaction.update(batchRef, { ...patch, updatedAt: serverTimestamp() });
    transaction.set(saleRef, { ...sale, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    return saleRef;
  });
};
export const reverseWorkflowRecord = async (collectionName, id) => {
  if (collectionName === 'batches') await ensureUsageInitialized();
  return runTransaction(db, async transaction => {
    const reference = getUserDoc(collectionName, id);
    const snapshot = await transaction.get(reference);
    if (!snapshot.exists()) return;
    const record = snapshot.data();
    let updates = [];
    let batchRef;
    let batchPatch;
    if (collectionName === 'purchases' && record.inventoryApplied === true && record.stockMovement) {
      const item = await transaction.get(getUserDoc('ingredients', record.stockMovement.id));
      if (!item.exists()) throw new Error('Restore the linked ingredient before reversing this purchase.');
      updates = [{ collection: 'ingredients', id: item.id, patch: planPurchaseReversal({ ...item.data(), id: item.id }, record.stockMovement) }];
    }
    if (collectionName === 'batches' && record.stockLedgerVersion === 1) {
      if (Number(record.soldBars || 0) > 0 || Number(record.remainingBars) !== Number(record.bars)) throw new Error('Delete the linked sales before reversing this production batch.');
      if (record.inventoryApplied) {
        const records = await recordsForRequirements(transaction, record.stockMovements || []);
        updates = planStockMovements(record.stockMovements || [], records, true).updates;
      }
    }
    if (collectionName === 'sales' && record.inventoryApplied === true && record.batchId) {
      batchRef = getUserDoc('batches', record.batchId);
      const batch = await transaction.get(batchRef);
      if (!batch.exists()) throw new Error('Restore the linked batch before reversing this sale.');
      batchPatch = planFinishedSale(batch.data(), record, true);
    }
    const usage = collectionName === 'batches' ? await readUsageChange(transaction, 'batches', -1, id) : null;
    applyStockUpdates(transaction, updates);
    if (batchRef) transaction.update(batchRef, { ...batchPatch, updatedAt: serverTimestamp() });
    transaction.delete(reference);
    writeUsageChange(transaction, usage);
  });
};

// Snapshot imports must not replay purchases, production or sales against stock.
// One capped resource per transaction keeps quota changes atomic with its creation.
export const importUserSnapshotDocument = async (collectionName, id, data) => {
  if (!['ingredients', 'packaging', 'suppliers', 'purchases', 'recipes', 'batches', 'sales', 'notes', 'reminders', 'notifications', 'settings'].includes(collectionName)) throw new Error('Unsupported backup collection.');
  if (QUOTA_COLLECTIONS.includes(collectionName)) await ensureUsageInitialized();
  return runTransaction(db, async transaction => {
    const reference = getUserDoc(collectionName, id);
    const existing = await transaction.get(reference);
    if (existing.exists()) {
      if (existing.data().importBackupId === data.importBackupId && existing.data().importOriginalId === data.importOriginalId) return false;
      throw new Error(`Restore stopped to avoid overwriting ${collectionName}/${id}. Your existing data is unchanged.`);
    }
    const usage = QUOTA_COLLECTIONS.includes(collectionName) ? await readUsageChange(transaction, collectionName, 1, id) : null;
    transaction.set(reference, { ...data, importedAt: serverTimestamp() });
    writeUsageChange(transaction, usage);
    return true;
  });
};

export const preflightSnapshotImport = async documents => {
  await ensureUsageInitialized();
  const [profile, ...collections] = await Promise.all([
    getDoc(doc(db, 'users', getCurrentUserId())),
    ...QUOTA_COLLECTIONS.map(collectionName => getUserDocs(collectionName)),
  ]);
  if (hasPaidAccess(profile.exists() ? profile.data() : null)) return;
  const limits = { recipes: FREE_PLAN_LIMITS.maxRecipes, ingredients: FREE_PLAN_LIMITS.maxIngredients, batches: FREE_PLAN_LIMITS.maxBatches };
  QUOTA_COLLECTIONS.forEach((collectionName, index) => {
    const existing = new Set(collections[index].map(item => item.id));
    const incoming = documents.filter(item => item.collection === collectionName && !existing.has(item.id)).length;
    if (existing.size + incoming > limits[collectionName]) throw new Error(`This restore would contain ${existing.size + incoming} ${collectionName}, above your free plan limit of ${limits[collectionName]}. No restore documents have been written. Choose a smaller backup or an active paid plan.`);
  });
};
